import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import { useAuthStore } from "@/stores/useAuthStore";
import type { Persona } from "@/mocks/dashboard/shared/placeholderData";

/**
 * Server-authoritative identity. Batch 27f split `name` → `first_name`
 * + `last_name` and added the `theme` preference column. The legacy
 * `name` field is GONE from the API contract; the top bar should
 * compose `${first_name} ${last_name}` (trim if either is empty).
 *
 * Phase 2.1 adds optional `onboarding_dismissed` flag — drives the
 * inline-coachmark banner on the Dashboard (shell-spec §5.2). Backend
 * field name confirmed in kickoff (POST /api/v1/users/me/dismiss-onboarding,
 * idempotent 204). Optional here so the field-missing branch (backend
 * lands later) defaults to "show banner."
 */
export type Me = {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_super_admin: boolean;
  tenant_id: string | null;
  tenant_slug: string | null;
  /**
   * Tenant/company display name — drives the paying-tenant first-run welcome
   * moment. Optional so older backends / SA sessions degrade gracefully.
   */
  tenant_name?: string | null;
  theme?: "light" | "dark" | "system";
  onboarding_dismissed?: boolean;
  /**
   * Phase 2.7 — runtime feature-flag envelope keyed by flag name.
   * The Resume tab reads ``feature_flags.resume_format`` to gate the
   * Format Resume button (I-KS-2). Optional so an older backend (or
   * the impersonation/legacy code paths) degrades gracefully — a
   * missing key is treated as "on" to avoid hiding the button by
   * accident during a partial deploy.
   */
  feature_flags?: Record<string, boolean>;
  /**
   * B-11 — demo tenant flag. True for tenants provisioned via the
   * SA demo dashboard (is_demo=true in the DB). Powers the demo banner
   * rendered by AppShell. Optional so older backends degrade gracefully.
   */
  tenant_is_demo?: boolean;
  /**
   * P1 (founder 2026-06-15) — SA-configured Hiring-Intelligence dashboard
   * variant for this tenant. The Intelligence dashboard renders ONLY this
   * variant (the user-facing toggle is removed). Backend always sends one of
   * `"agency" | "in_house"`; optional here so older backends / SA sessions
   * degrade gracefully (callers default to `"in_house"`, the universal default).
   */
  dashboard_persona?: Persona;
  /**
   * RBAC WS-1 — server-computed capability set for this user's role.
   * Frontend gates affordances (create buttons, nav links, settings, user-mgmt)
   * on this list. NEVER re-derive from `role` — the backend is the single
   * source of truth. Optional so older backends degrade gracefully.
   */
  capabilities?: string[];
};

/**
 * Server-authoritative identity for the current session via GET /auth/me.
 * Skips the request entirely until useAuthStore says we're authenticated so
 * the admin top bar doesn't fire a 401 against the public signup flow.
 */
export function useMe() {
  const status = useAuthStore((s) => s.status);
  return useQuery<Me, ApiError>({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      const resp = await apiClient.get<Me>("/auth/me");
      return resp.data;
    },
    enabled: status === "authenticated",
    staleTime: 5 * 60_000,
  });
}
